/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable no-nested-ternary */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/naming-convention */
import LZString from 'lz-string';

import {
  DriveInfo,
  DriveSensorInfo,
  OrgFilterItem,
  RawDriveSensorInfo,
  RawDriveInfo,
  SDKSensorInfoAugmented,
} from '../types';
import { sanitizeRecordingFilters } from './recordingFilterKeys';

const convertUTCStringToDate = (dateStr: string): Date =>
  new Date(`${dateStr}Z`);

export const publicationUuidToURL = (uuid: string): string =>
  `${window.location.origin}/share/${uuid}`;

const transformSensorInfos = (
  sensors: RawDriveSensorInfo[]
): DriveSensorInfo[] =>
  /* 
    TODO: (vivian)
    Sensor is sorted by its serial number so the multi-sensor data can be handled gracefully as single sensor data. This step is not needed after we fully implemented the multi-sensor viz
  */
  sensors
    .map((sensor) => ({
      sdkFwBranch: sensor.sdk_fw_branch,
      sdkFwBuildDate: sensor.sdk_fw_build_date
        ? convertUTCStringToDate(sensor.sdk_fw_build_date)
        : null,
      sdkFwBuildRev: sensor.sdk_fw_build_rev,
      sdkFwCommitHash: sensor.sdk_fw_commit_hash,
      sdkFwImageRev: sensor.sdk_fw_image_rev,
      sdkFwMergeRequest: sensor.sdk_fw_merge_request,
      sdkFwVersionCore: sensor.sdk_fw_version_core,
      sdkFwVersionPrerelease: sensor.sdk_fw_version_prerelease,
      sdkLidarMode: sensor.sdk_lidar_mode,
      sdkProductBeamSpacing: sensor.sdk_prod_beam_spacing,
      sdkProductGeneration: sensor.sdk_prod_generation,
      sdkProductHwRevision: sensor.sdk_prod_hw_rev,
      sdkProductLine: sensor.sdk_prod_line,
      sdkProductPartNumber: sensor.sdk_prod_pn,
      sdkProductResolution: sensor.sdk_prod_resolution,
      sdkProductSerialNumber: sensor.sdk_prod_sn,
      sdkProductType: sensor.sdk_prod_type,
    }))
    .sort((a, b) => {
      const snA = a.sdkProductSerialNumber?.toString() || '';
      const snB = b.sdkProductSerialNumber?.toString() || '';
      if (snA === snB) {
        return 0;
      }
      if (!snA) {
        return 1; // If snA is undefined, place it after snB
      }
      if (!snB) {
        return -1; // If snB is undefined, place it after snA
      }
      return snA.localeCompare(snB);
    });

/* 
  This method is used for viz page and on list page, we need less recording info.
  The transform method for list page has been moved to component lib, leave this one here for viz page usage
*/
export const transformRawDrives: (raw: RawDriveInfo) => DriveInfo = (raw) => {
  const startTime = raw.start_time ? new Date(`${raw.start_time}Z`) : '...';
  const stopTime = raw.stop_time ? new Date(`${raw.stop_time}Z`) : '...';

  return {
    id: raw.id,
    dataType: raw.data_type,
    driveOrigin: raw.drive_origin,
    organizationId: raw.organization_id,
    startTime,
    stopTime,
    uploadStatus: raw.upload_status,
    dataCategory: raw.data_category,
    sourceBucket: raw.source_bucket,
    sourcePrefix: raw.source_prefix,
    processedAt: convertUTCStringToDate(raw.processed_at),
    osfGenerationStatus: raw.osf_generation_status,
    osfGenerationDate: raw.osf_generation_date
      ? convertUTCStringToDate(raw.osf_generation_date)
      : null,
    osfBucket: raw.osf_bucket,
    osfPrefix: raw.osf_prefix,
    slamGenerationStatus: raw.slam_generation_status,
    slamGenerationDate: raw.slam_generation_date
      ? convertUTCStringToDate(raw.slam_generation_date)
      : null,
    slamBucket: raw.slam_bucket,
    slamPrefix: raw.slam_prefix,
    uploadedBy: raw.uploaded_by,
    uploadedById: raw.uploaded_by_id,
    publicTitle: raw.public_title,
    accessLevel: raw.access_level,
    publicURL: raw.publication_uuid
      ? publicationUuidToURL(raw.publication_uuid)
      : undefined,
    sensorInfos: transformSensorInfos(raw.sensor_info),
    tags: raw.tags,
    visualization_status: raw.visualization_status,
    organizationName: raw.organization_name,
    recordingTitle: raw.recording_title,
  };
};

export const formatDateTime = (dateStr: string): string => {
  const date = new Date(dateStr);
  return `${date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })} | ${date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })}`;
};

export const convertFilterArrayToObject = (
  data: OrgFilterItem[]
): Record<string, string[]> => {
  const result: Record<string, string[]> = {};

  data.forEach((item) => {
    result[item.field_name] = item.values
      .filter((value) => value !== null)
      .map((value) => String(value));
  });

  return result;
};

export const parseUrlParams = (search: string): Record<string, string[]> => {
  const params = new URLSearchParams(search);
  const compressedFilters = params.get('filters');

  const filterObject: Record<string, string[]> = {};

  // Convert old-style filters to new format
  params.forEach((value, key) => {
    if (
      key !== 'filters' &&
      key !== 'order_by' &&
      key !== 'sort_order' &&
      key !== 'page' &&
      key !== 'q'
    ) {
      if (filterObject[key]) {
        filterObject[key].push(value);
      } else {
        filterObject[key] = [value];
      }
    }
  });

  // If compressed filters exist, add them to the filterObject
  if (compressedFilters) {
    try {
      const decompressed =
        LZString.decompressFromEncodedURIComponent(compressedFilters);
      if (decompressed) {
        const existingFilters = JSON.parse(decompressed) as Record<
          string,
          string[]
        >;
        // Merge with existing filters
        Object.keys(existingFilters).forEach((key) => {
          if (filterObject[key]) {
            filterObject[key] = [
              ...new Set([...filterObject[key], ...existingFilters[key]]),
            ];
          } else {
            filterObject[key] = existingFilters[key];
          }
        });
      }
    } catch (error) {
      console.error('Error decompressing filters:', error);
    }
  }

  return sanitizeRecordingFilters(filterObject);
};

/**
 * Converts a single DriveSensorInfo object to RawDriveSensorInfo format
 */
export function driveSensorInfoToRaw(
  sensorInfo: DriveSensorInfo
): RawDriveSensorInfo {
  return {
    sdk_fw_branch: sensorInfo.sdkFwBranch,
    sdk_fw_build_date: sensorInfo.sdkFwBuildDate?.toISOString() || null,
    sdk_fw_build_rev: sensorInfo.sdkFwBuildRev,
    sdk_fw_commit_hash: sensorInfo.sdkFwCommitHash,
    sdk_fw_image_rev: sensorInfo.sdkFwImageRev,
    sdk_fw_merge_request: sensorInfo.sdkFwMergeRequest,
    sdk_fw_version_core: sensorInfo.sdkFwVersionCore,
    sdk_fw_version_prerelease: sensorInfo.sdkFwVersionPrerelease,
    sdk_lidar_mode: sensorInfo.sdkLidarMode,
    sdk_prod_beam_spacing: sensorInfo.sdkProductBeamSpacing,
    sdk_prod_generation: sensorInfo.sdkProductGeneration,
    sdk_prod_hw_rev: sensorInfo.sdkProductHwRevision,
    sdk_prod_line: sensorInfo.sdkProductLine,
    sdk_prod_pn: sensorInfo.sdkProductPartNumber,
    sdk_prod_resolution: sensorInfo.sdkProductResolution,
    sdk_prod_sn: sensorInfo.sdkProductSerialNumber,
    sdk_prod_type: sensorInfo.sdkProductType,
  };
}

/**
 * Converts an array of DriveSensorInfo objects to RawDriveSensorInfo format
 */
export function driveSensorInfoArrayToRaw(
  sensorInfoArray: DriveSensorInfo[]
): RawDriveSensorInfo[] {
  return sensorInfoArray.map(driveSensorInfoToRaw);
}

/**
 * Finds the sensor entry in drive metadata that matches a product serial number.
 *
 * @param productSn - Product serial number extracted from worker sensor info.
 * @param driveInfo - Drive metadata object that may contain `sensorInfos`.
 * @returns The matching sensor metadata object when found; otherwise `null`.
 */
function findMatchingSensorInfo(
  productSn: string | undefined,
  driveInfo?: Pick<DriveInfo, 'sensorInfos'> | null
): DriveSensorInfo | null {
  const sensorInfos = driveInfo?.sensorInfos;
  if (!Array.isArray(sensorInfos) || !productSn) return null;

  return (
    sensorInfos.find(
      (sensorInfo) => sensorInfo.sdkProductSerialNumber === productSn
    ) ?? null
  );
}

/**
 * Format product line by removing the first dash between model and number
 *
 * e.g. 'OS-1-128' will become 'OS1-128'
 *
 * @param prodLine - The product line string to format
 * @returns Formatted product line string with first dash removed
 */
export const formatProdLine = (prodLine: string): string => {
  if (prodLine.includes('-')) {
    return prodLine.replace(/^([A-Z]+)-(\d+)/, '$1$2');
  }
  return prodLine;
};

const validateUdpProfileLidar = (profile?: string): string => {
  const validProfiles = [
    'LEGACY',
    'RNG19_RFL8_SIG16_NIR16',
    'RNG19_RFL8_SIG16_NIR16_DUAL',
    'RNG15_RFL8_NIR8',
    'RNG15_RFL8_NIR8_DUAL',
    'FUSA_RNG15_RFL8_NIR8_DUAL',
    'RNG15_RFL8_NIR8_ZONE16',
    'RNG19_RFL8_SIG16_NIR16_ZONE16',
    'RNG19_RFL8_SIG16_NIR16_RGB16',
    'RNG19_RFL8_SIG16_NIR16_RGB16_DUAL',
  ];
  const defaultProfile = 'RNG19_RFL8_SIG16_NIR16';
  return profile && validProfiles.includes(profile) ? profile : defaultProfile;
};

/**
 * Normalizes worker sensor metadata into the `SDKSensorInfoAugmented` shape.
 *
 * @param raw - Sensor metadata payload emitted by the worker.
 * @param driveInfo - Drive metadata used to enrich missing fields.
 * @returns A normalized `SDKSensorInfoAugmented` object.
 */
export function normalizeSensorInfoFromWorker(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  raw: any,
  driveInfo?: Pick<DriveInfo, 'sensorInfos'> | null
): SDKSensorInfoAugmented {
  const s = raw && typeof raw === 'object' ? raw : {};
  const productSnBase: string =
    typeof s.productSn === 'string'
      ? s.productSn
      : typeof s.sn === 'string'
      ? s.sn
      : s.product_sn != null
      ? String(s.product_sn)
      : s.prod_sn != null
      ? String(s.prod_sn)
      : '';
  const matchingSensorInfo = productSnBase
    ? findMatchingSensorInfo(productSnBase, driveInfo)
    : null;
  const firstDriveSensorInfo = driveInfo?.sensorInfos?.[0];
  const config = s.config ?? s.config_params ?? {};
  const rawMode = s.mode ?? s.lidar_mode ?? s.lidarMode ?? config.lidar_mode;
  const rawProdLine =
    s.prod_line ??
    s.prodLine ??
    matchingSensorInfo?.sdkProductLine ??
    firstDriveSensorInfo?.sdkProductLine;
  const baseFormat = s.format ?? s.lidar_data_format ?? s.data_format ?? {};
  const normalizedSn =
    s.sn ?? s.productSn ?? s.product_sn ?? s.prod_sn ?? productSnBase;
  const normalizedFwRev =
    matchingSensorInfo?.sdkFwVersionCore ??
    firstDriveSensorInfo?.sdkFwVersionCore ??
    s.fw_rev ??
    s.fwRev ??
    s.build_rev ??
    s.buildRevision;
  const normalizedImageRev =
    matchingSensorInfo?.sdkFwImageRev ??
    firstDriveSensorInfo?.sdkFwImageRev ??
    s.image_rev ??
    s.imageRev;
  const normalizedHwRev =
    matchingSensorInfo?.sdkProductHwRevision ??
    firstDriveSensorInfo?.sdkProductHwRevision ??
    s.hw_rev ??
    null;
  const normalizedFormat = {
    column_window:
      baseFormat.column_window ?? baseFormat.columnWindow ?? undefined,
    columns_per_frame:
      baseFormat.columns_per_frame ?? baseFormat.columnsPerFrame ?? undefined,
    columns_per_packet:
      baseFormat.columns_per_packet ?? baseFormat.columnsPerPacket ?? undefined,
    pixel_shift_by_row:
      baseFormat.pixel_shift_by_row ?? baseFormat.pixelShiftByRow ?? undefined,
    pixels_per_column:
      baseFormat.pixels_per_column ?? baseFormat.pixelsPerColumn ?? undefined,
    udp_profile_imu:
      baseFormat.udp_profile_imu ??
      baseFormat.udpProfileImu ??
      config.udp_profile_imu ??
      config.udpProfileImu ??
      'LEGACY',
    udp_profile_lidar: validateUdpProfileLidar(
      (baseFormat.udp_profile_lidar as string | undefined) ??
        (baseFormat.udpProfileLidar as string | undefined) ??
        (config.udp_profile_lidar as string | undefined) ??
        (config.udpProfileLidar as string | undefined)
    ),
  };
  const normalizedAzimuthWindow =
    s.azimuth_window ??
    s.azimuthWindow ??
    config.azimuth_window ??
    config.azimuthWindow ??
    undefined;
  const normalizedBeamAltitudeAngles =
    s.beam_altitude_angles ?? s.beamAltitudeAngles ?? undefined;
  const normalizedBeamAzimuthAngles =
    s.beam_azimuth_angles ?? s.beamAzimuthAngles ?? undefined;
  const normalizedMode = rawMode != null ? String(rawMode) : '';
  const normalized: SDKSensorInfoAugmented = {
    sn: normalizedSn,
    fw_rev: normalizedFwRev,
    image_rev: normalizedImageRev,
    hw_rev: normalizedHwRev,
    mode: rawMode,
    prod_line:
      rawProdLine != null
        ? formatProdLine(String(rawProdLine))
        : s.prod_line ?? s.prodLine,
    format: normalizedFormat,
    azimuth_window: normalizedAzimuthWindow,
    beam_altitude_angles: normalizedBeamAltitudeAngles,
    beam_azimuth_angles: normalizedBeamAzimuthAngles,
    beam_to_lidar_transform: s.beam_to_lidar_transform ?? undefined,
    lidar_to_sensor_transform: s.lidar_to_sensor_transform ?? undefined,
    lidar_origin_to_beam_origin_mm:
      s.lidar_origin_to_beam_origin_mm ??
      s.lidarOriginToBeamOrigin ??
      undefined,
    init_id:
      s.init_id ??
      s.initializationId ??
      s.sensorInfo?.initializationId ??
      undefined,
    udp_port_imu: s.udp_port_imu ?? s.udpPortImu ?? undefined,
    udp_port_lidar: s.udp_port_lidar ?? s.udpPortLidar ?? undefined,
    prod_pn:
      matchingSensorInfo?.sdkProductPartNumber ??
      firstDriveSensorInfo?.sdkProductPartNumber ??
      s.prod_pn ??
      s.prodPn ??
      s.productPn ??
      s.product_pn ??
      undefined,
    prod_resolution:
      s.prod_resolution ??
      s.prodResolution ??
      matchingSensorInfo?.sdkProductResolution?.toString() ??
      firstDriveSensorInfo?.sdkProductResolution?.toString() ??
      undefined,
    prod_type:
      s.prod_type ??
      s.prodType ??
      matchingSensorInfo?.sdkProductType ??
      firstDriveSensorInfo?.sdkProductType ??
      undefined,
    signal_multiplier:
      s.signal_multiplier ??
      s.signalMultiplier ??
      config.signal_multiplier ??
      config.signalMultiplier ??
      undefined,
    calibration_status:
      s.calibration_status ?? s.calibrationStatus ?? undefined,
    extrinsics: s.extrinsics,
  };

  // Compatibility aliases for existing reducers/components that still read
  // metadata fields in camelCase or legacy nested structures.
  const normalizedProductSn =
    typeof normalized.sn === 'string' ? normalized.sn : String(normalized.sn);
  return {
    ...normalized,
    productSn: normalizedProductSn,
    lidarMode: normalizedMode,
    buildRev: normalizedFwRev,
    imageRev: normalizedImageRev,
    hwRev: normalizedHwRev,
    beamAltitudeDegrees: normalizedBeamAltitudeAngles,
    beamAzimuthDegrees: normalizedBeamAzimuthAngles,
    dataFormat: {
      columnWindow: normalizedFormat.column_window,
      columnsPerFrame: normalizedFormat.columns_per_frame,
      columnsPerPacket: normalizedFormat.columns_per_packet,
      pixelsPerColumn: normalizedFormat.pixels_per_column,
      pixelShiftByRow: normalizedFormat.pixel_shift_by_row,
      udpProfileLidar: normalizedFormat.udp_profile_lidar,
      udpProfileImu: normalizedFormat.udp_profile_imu,
    },
    configParams: {
      lidarMode: normalizedMode,
      udpProfileLidar: normalizedFormat.udp_profile_lidar,
      azimuthWindow: normalizedAzimuthWindow,
      signalMultiplier: normalized.signal_multiplier,
    },
    sensorInfo: {
      prodSn: normalizedProductSn,
      buildRev: normalizedFwRev,
      imageRev: normalizedImageRev,
      hwRev: normalizedHwRev,
      status: s.status ?? s.sensorInfo?.status ?? 'UNKNOWN',
    },
  } as SDKSensorInfoAugmented;
}
